<template>
	<div class="dashboard-container">
		<!-- 欢迎区域 -->
		<div class="welcome-section">
			<div class="welcome-content">
				<h1 class="welcome-title">欢迎回来，{{$storage.get('adminName')}}</h1>
				<p class="welcome-subtitle">医疗管理系统数据概览</p>
			</div>
			<div class="welcome-time">
				<span class="current-time">{{currentTime}}</span>
			</div>
		</div>

		<!-- 统计卡片区域 -->
		<div class="stats-section">
			<div class="stats-grid">
				<div class="stat-card">
					<div class="stat-content">
						<div class="stat-info">
							<h3 class="stat-title">总订单数</h3>
							<p class="stat-value">{{totalOrders}}</p>
							<span class="stat-change positive">+{{todayOrders}} 今日新增</span>
						</div>
						<div class="stat-icon orders">
							<svg viewBox="0 0 24 24">
								<path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L10.83,7.75L13.41,10.34L16,6M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2Z"/>
							</svg>
						</div>
					</div>
				</div>

				<div class="stat-card">
					<div class="stat-content">
						<div class="stat-info">
							<h3 class="stat-title">月销售额</h3>
							<p class="stat-value">¥{{monthRevenue}}</p>
							<span class="stat-change positive">+{{monthGrowth}}% 环比上月</span>
						</div>
						<div class="stat-icon revenue">
							<svg viewBox="0 0 24 24">
								<path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M11,17H13V16H14A2,2 0 0,0 16,14V13A2,2 0 0,0 14,11H10V10H16V8H13V7H11V8H10A2,2 0 0,0 8,10V11A2,2 0 0,0 10,13H14V14H8V16H11V17Z"/>
							</svg>
						</div>
					</div>
				</div>

				<div class="stat-card">
					<div class="stat-content">
						<div class="stat-info">
							<h3 class="stat-title">活跃用户</h3>
							<p class="stat-value">{{activeUsers}}</p>
							<span class="stat-change negative">-{{userDecrease}}% 较昨日</span>
						</div>
						<div class="stat-icon users">
							<svg viewBox="0 0 24 24">
								<path d="M16,4C18.21,4 20,5.79 20,8C20,10.21 18.21,12 16,12C13.79,12 12,10.21 12,8C12,5.79 13.79,4 16,4M16,14C20.42,14 24,15.79 24,18V20H8V18C8,15.79 11.58,14 16,14M8,4C10.21,4 12,5.79 12,8C12,10.21 10.21,12 8,12C5.79,12 4,10.21 4,8C4,5.79 5.79,4 8,4M8,14C12.42,14 16,15.79 16,18V20H0V18C0,15.79 3.58,14 8,14Z"/>
							</svg>
						</div>
					</div>
				</div>

				<div class="stat-card">
					<div class="stat-content">
						<div class="stat-info">
							<h3 class="stat-title">库存预警</h3>
							<p class="stat-value">{{lowStockItems}}</p>
							<span class="stat-change warning">需要补货</span>
						</div>
						<div class="stat-icon stock">
							<svg viewBox="0 0 24 24">
								<path d="M12,2A1,1 0 0,1 13,3V9A1,1 0 0,1 12,10A1,1 0 0,1 11,9V3A1,1 0 0,1 12,2M21,11A1,1 0 0,1 22,12A1,1 0 0,1 21,13H15A1,1 0 0,1 14,12A1,1 0 0,1 15,11H21M19.79,7.75L18.38,9.16L14.34,5.12L15.75,3.71L19.79,7.75M4.21,7.75L8.25,3.71L9.66,5.12L5.62,9.16L4.21,7.75M13,14H22L21,16L22,18H13C13,16.67 13,15.33 13,14M5,14C7.67,14 10.33,14 13,14C13,15.81 13,17.23 13,19H5C5,17.23 5,15.81 5,14M3,14H5C5,15.81 5,17.23 5,19H3C3,17.23 3,15.81 3,14Z"/>
							</svg>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- 图表区域 -->
		<div class="charts-section">
			<div class="charts-grid">
				<!-- 营收趋势图 -->
				<div class="chart-card">
					<div class="chart-header">
						<h3 class="chart-title">营收趋势</h3>
						<div class="chart-controls">
							<button 
								class="time-btn" 
								:class="{active: revenueTimeRange === '7d'}"
								@click="changeRevenueTimeRange('7d')"
							>7天</button>
							<button 
								class="time-btn" 
								:class="{active: revenueTimeRange === '30d'}"
								@click="changeRevenueTimeRange('30d')"
							>30天</button>
							<button 
								class="time-btn" 
								:class="{active: revenueTimeRange === '90d'}"
								@click="changeRevenueTimeRange('90d')"
							>90天</button>
						</div>
					</div>
					<div class="chart-content">
						<div ref="revenueChart" class="chart-container"></div>
					</div>
				</div>

				<!-- 订单统计图 -->
				<div class="chart-card">
					<div class="chart-header">
						<h3 class="chart-title">订单统计</h3>
						<div class="chart-legend">
							<span class="legend-item">
								<span class="legend-dot pending"></span>
								待处理
							</span>
							<span class="legend-item">
								<span class="legend-dot completed"></span>
								已完成
							</span>
							<span class="legend-item">
								<span class="legend-dot cancelled"></span>
								已取消
							</span>
						</div>
					</div>
					<div class="chart-content">
						<div ref="orderChart" class="chart-container"></div>
					</div>
				</div>
			</div>
		</div>


</div>
</template>
<script>
import router from '@/router/router-static'
import * as echarts from 'echarts'

export default {
	data() {
		return {
			// 时间相关
			currentTime: '',
			timeInterval: null,
			
			// 统计数据
			totalOrders: 0,
			todayOrders: 0,
			monthRevenue: '0',
			monthGrowth: 0,
			activeUsers: 0,
			userDecrease: 0,
			lowStockItems: 0,
			
			// 图表相关
			revenueTimeRange: '30d',
			revenueChart: null,
			orderChart: null,
			

		};
	},
	
	mounted() {
    this.init();
		this.initTime();
		this.initCharts();
		this.loadDashboardData();
	},
	
	beforeDestroy() {
		if (this.timeInterval) {
			clearInterval(this.timeInterval);
		}
		if (this.revenueChart) {
			this.revenueChart.dispose();
		}
		if (this.orderChart) {
			this.orderChart.dispose();
		}
  },
	
	methods: {
		// 原有的认证初始化
		init() {
			if (this.$storage.get('Token')) {
        this.$http({
            url: `${this.$storage.get('sessionTable')}/session`,
            method: "get"
        }).then(({ data }) => {
            if (data && data.code != 0) {
						router.push({ name: 'login' });
            }
        });
			} else {
				router.push({ name: 'login' });
        }
    },
		
		// 初始化时间显示
		initTime() {
			this.updateTime();
			this.timeInterval = setInterval(this.updateTime, 1000);
		},
		
		// 更新时间
		updateTime() {
			const now = new Date();
			const options = {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
				weekday: 'long'
			};
			this.currentTime = now.toLocaleDateString('zh-CN', options);
		},
		
		// 初始化图表
		initCharts() {
			this.$nextTick(() => {
				this.initRevenueChart();
				this.initOrderChart();
			});
		},
		
		// 初始化营收趋势图
		initRevenueChart() {
			if (this.$refs.revenueChart) {
				this.revenueChart = echarts.init(this.$refs.revenueChart);
				this.updateRevenueChart();
			}
		},
		
		// 初始化订单统计图
		initOrderChart() {
			if (this.$refs.orderChart) {
				this.orderChart = echarts.init(this.$refs.orderChart);
				this.updateOrderChart();
			}
		},
		
		// 更新营收趋势图
		updateRevenueChart() {
			// 从真实订单数据计算营收趋势
			this.$http({
				url: 'orders/page',
				method: 'get',
				params: {
					page: 1,
					limit: 1000 // 获取所有订单来计算营收
				}
			}).then(({ data }) => {
				if (data && data.code === 0) {
					const orders = data.data.list || [];
					const chartData = this.calculateRevenueData(orders, this.revenueTimeRange);
					this.renderRevenueChart(chartData);
				}
			}).catch(error => {
				console.error('加载营收趋势数据失败:', error);
				// 如果加载失败，使用本地模拟数据
				const data = this.getRevenueData(this.revenueTimeRange);
				this.renderRevenueChart(data);
			});
		},
		
		// 渲染营收趋势图
		renderRevenueChart(data) {
			const option = {
				tooltip: {
					trigger: 'axis',
					backgroundColor: '#ffffff',
					borderColor: '#e2e8f0',
					borderWidth: 1,
					textStyle: {
						color: '#374151'
					}
				},
				grid: {
					left: '3%',
					right: '3%',
					bottom: '3%',
					top: '3%',
					containLabel: true
				},
				xAxis: {
					type: 'category',
					data: data.dates,
					axisLine: {
						lineStyle: {
							color: '#e2e8f0'
						}
					},
					axisTick: {
						show: false
					},
					axisLabel: {
						color: '#64748b',
						fontSize: 12
					}
				},
				yAxis: {
					type: 'value',
					axisLine: {
						show: false
					},
					axisTick: {
						show: false
					},
					axisLabel: {
						color: '#64748b',
						fontSize: 12,
						formatter: '{value}k'
					},
					splitLine: {
						lineStyle: {
							color: '#f1f5f9'
						}
					}
				},
				series: [{
					name: '营收',
					type: 'line',
					data: data.values,
					smooth: true,
					lineStyle: {
						color: '#3b82f6',
						width: 3
					},
					itemStyle: {
						color: '#3b82f6'
					},
					areaStyle: {
						color: {
							type: 'linear',
							x: 0,
							y: 0,
							x2: 0,
							y2: 1,
							colorStops: [{
								offset: 0,
								color: 'rgba(59, 130, 246, 0.3)'
							}, {
								offset: 1,
								color: 'rgba(59, 130, 246, 0.05)'
							}]
						}
					}
				}]
			};
			this.revenueChart.setOption(option);
		},
		
		// 更新订单统计图
		updateOrderChart() {
			// 从现有API获取订单数据并统计
			this.$http({
				url: 'orders/page',
				method: 'get',
				params: {
					page: 1,
					limit: 1000 // 获取所有订单来统计状态
				}
			}).then(({ data }) => {
				if (data && data.code === 0) {
					const orders = data.data.list || [];
					
					// 统计各种状态的订单数量
					const statusCount = {
						'已完成': 0,
						'待处理': 0,
						'已取消': 0
					};
					
					orders.forEach(order => {
						const status = order.status;
						if (status === '已完成') {
							statusCount['已完成']++;
						} else if (['未支付', '已支付', '已发货'].includes(status)) {
							statusCount['待处理']++;
						} else if (status === '已取消') {
							statusCount['已取消']++;
						}
					});
					
					const chartData = [
						{ 
							value: statusCount['已完成'], 
							name: '已完成',
							itemStyle: { color: '#10b981' }
						},
						{ 
							value: statusCount['待处理'], 
							name: '待处理',
							itemStyle: { color: '#f59e0b' }
						},
						{ 
							value: statusCount['已取消'], 
							name: '已取消',
							itemStyle: { color: '#ef4444' }
						}
					];
					
					this.renderOrderChart(chartData);
				}
			}).catch(error => {
				console.error('加载订单统计数据失败:', error);
				// 使用默认数据
				const defaultData = [
					{ 
						value: 0, 
						name: '已完成',
						itemStyle: { color: '#10b981' }
					},
					{ 
						value: 0, 
						name: '待处理',
						itemStyle: { color: '#f59e0b' }
					},
					{ 
						value: 0, 
						name: '已取消',
						itemStyle: { color: '#ef4444' }
					}
				];
				this.renderOrderChart(defaultData);
			});
		},
		
		// 渲染订单统计图
		renderOrderChart(data) {
			// 为每个数据项添加颜色配置
			const colorMap = {
				'已完成': '#10b981',
				'待处理': '#f59e0b',
				'已取消': '#ef4444'
			};
			
			data.forEach(item => {
				if (!item.itemStyle) {
					item.itemStyle = { color: colorMap[item.name] || '#64748b' };
				}
			});
			
			const option = {
				tooltip: {
					trigger: 'item',
					backgroundColor: '#ffffff',
					borderColor: '#e2e8f0',
					borderWidth: 1,
					textStyle: {
						color: '#374151'
					}
				},
				series: [{
					name: '订单状态',
					type: 'pie',
					radius: ['40%', '70%'],
					avoidLabelOverlap: false,
					label: {
						show: false,
						position: 'center'
					},
					emphasis: {
						label: {
							show: true,
							fontSize: '20',
							fontWeight: 'bold',
							color: '#374151'
						}
					},
					labelLine: {
						show: false
					},
					data: data
				}]
			};
			this.orderChart.setOption(option);
		},
		
		// 计算真实营收数据
		calculateRevenueData(orders, timeRange) {
			const now = new Date();
			let dates = [];
			let values = [];
			
			if (timeRange === '7d') {
				// 最近7天
				for (let i = 6; i >= 0; i--) {
					const date = new Date(now);
					date.setDate(date.getDate() - i);
					dates.push(this.formatDate(date, 'MM-dd'));
					
					// 计算当天的营收
					const dayRevenue = this.calculateDayRevenue(orders, date);
					values.push(dayRevenue);
				}
			} else if (timeRange === '30d') {
				// 最近6个月
				for (let i = 5; i >= 0; i--) {
					const date = new Date(now);
					date.setMonth(date.getMonth() - i);
					dates.push(this.formatDate(date, 'MM月'));
					
					// 计算当月的营收
					const monthRevenue = this.calculateMonthRevenue(orders, date);
					values.push(monthRevenue);
				}
			} else {
				// 90d - 按季度（简化为最近4个月）
				for (let i = 3; i >= 0; i--) {
					const date = new Date(now);
					date.setMonth(date.getMonth() - i);
					dates.push('Q' + Math.ceil((date.getMonth() + 1) / 3));
					
					const monthRevenue = this.calculateMonthRevenue(orders, date);
					values.push(monthRevenue);
				}
			}
			
			return { dates, values };
		},
		
		// 计算某天的营收
		calculateDayRevenue(orders, targetDate) {
			const targetDateStr = this.formatDate(targetDate, 'yyyy-MM-dd');
			let revenue = 0;
			
			orders.forEach(order => {
				// 只计算已支付和已完成的订单营收
				if (order.addtime && ['已支付', '已完成', '已发货'].includes(order.status) && order.total) {
					const orderDate = new Date(order.addtime);
					const orderDateStr = this.formatDate(orderDate, 'yyyy-MM-dd');
					if (orderDateStr === targetDateStr) {
						revenue += parseFloat(order.total) || 0;
					}
				}
			});
			
			return Math.round(revenue / 1000 * 100) / 100; // 转换为k单位，保留两位小数
		},
		
		// 计算某月的营收
		calculateMonthRevenue(orders, targetDate) {
			const year = targetDate.getFullYear();
			const month = targetDate.getMonth();
			let revenue = 0;
			
			orders.forEach(order => {
				// 只计算已支付和已完成的订单营收
				if (order.addtime && ['已支付', '已完成', '已发货'].includes(order.status) && order.total) {
					const orderDate = new Date(order.addtime);
					if (orderDate.getFullYear() === year && orderDate.getMonth() === month) {
						revenue += parseFloat(order.total) || 0;
					}
				}
			});
			
			return Math.round(revenue / 1000 * 100) / 100; // 转换为k单位，保留两位小数
		},
		
		// 格式化日期
		formatDate(date, format) {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			
			switch (format) {
				case 'yyyy-MM-dd':
					return `${year}-${month}-${day}`;
				case 'MM-dd':
					return `${month}-${day}`;
				case 'MM月':
					return `${month}月`;
				default:
					return `${year}-${month}-${day}`;
			}
		},
		
		// 获取营收数据（备用模拟数据）
		getRevenueData(timeRange) {
			const data = {
				'7d': {
					dates: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
					values: [12, 19, 15, 27, 32, 28, 35]
				},
				'30d': {
					dates: ['1月', '2月', '3月', '4月', '5月', '6月'],
					values: [65, 78, 82, 94, 105, 125]
				},
				'90d': {
					dates: ['Q1', 'Q2', 'Q3', 'Q4'],
					values: [225, 287, 312, 398]
				}
			};
			return data[timeRange] || data['30d'];
		},
		
		// 改变营收时间范围
		changeRevenueTimeRange(range) {
			this.revenueTimeRange = range;
			this.updateRevenueChart();
		},
		
		// 加载仪表板数据
		loadDashboardData() {
			// 加载统计数据
			this.loadStats();
		},
		
		// 加载统计数据
		loadStats() {
			// 使用现有的API接口获取统计数据
			this.loadOrderStats();
			this.loadUserStats();
			this.loadProductStats();
		},
		
		// 加载订单统计
		loadOrderStats() {
			this.$http({
				url: 'orders/page',
				method: 'get',
				params: {
					page: 1,
					limit: 1000 // 获取所有订单来计算真实数据
				}
			}).then(({ data }) => {
				if (data && data.code === 0) {
					const orders = data.data.list || [];
					this.totalOrders = data.data.total || 0;
					
					// 计算今日订单数
					const today = new Date();
					const todayStr = this.formatDate(today, 'yyyy-MM-dd');
					this.todayOrders = orders.filter(order => {
						if (order.addtime) {
							const orderDate = new Date(order.addtime);
							return this.formatDate(orderDate, 'yyyy-MM-dd') === todayStr;
						}
						return false;
					}).length;
					
					// 计算本月销售额
					const currentMonth = new Date();
					const monthRevenue = this.calculateMonthRevenue(orders, currentMonth);
					this.monthRevenue = this.formatNumber((monthRevenue * 1000).toString()); // 转换回原始金额
					this.monthGrowth = 15.3; // 暂时使用固定增长率
				}
			}).catch(error => {
				console.error('加载订单统计失败:', error);
				// 使用默认值
				this.totalOrders = 0;
				this.todayOrders = 0;
				this.monthRevenue = '0';
				this.monthGrowth = 0;
			});
		},
		
		// 加载用户统计
		loadUserStats() {
			this.$http({
				url: 'yonghu/page',
				method: 'get',
				params: {
					page: 1,
					limit: 1
				}
			}).then(({ data }) => {
				if (data && data.code === 0) {
					this.activeUsers = data.data.total || 0;
					this.userDecrease = 2.1;
				}
			}).catch(error => {
				console.error('加载用户统计失败:', error);
				this.activeUsers = 0;
				this.userDecrease = 0;
			});
		},
		
		// 加载商品统计
		loadProductStats() {
			this.$http({
				url: 'rexiaonongchanpin/page',
				method: 'get',
				params: {
					page: 1,
					limit: 1000 // 获取所有商品来统计库存
				}
			}).then(({ data }) => {
				if (data && data.code === 0) {
					const products = data.data.list || [];
					// 统计库存预警商品（库存<=10且不是无限制）
					const lowStockCount = products.filter(product => 
						product.alllimittimes !== -1 && product.alllimittimes <= 10
					).length;
					this.lowStockItems = lowStockCount;
				}
			}).catch(error => {
				console.error('加载商品统计失败:', error);
				this.lowStockItems = 0;
			});
		},
		

		
		// 格式化数字
		formatNumber(num) {
			if (!num) return '0';
			const number = parseFloat(num);
			if (number >= 10000) {
				return (number / 10000).toFixed(1) + '万';
			} else if (number >= 1000) {
				return (number / 1000).toFixed(1) + 'k';
			}
			return number.toFixed(2);
		},
		

  }
};
</script>
<style lang="scss" scoped>
.dashboard-container {
	padding: 0;
	background: #f8fafc;
	min-height: 100%;
}

// 欢迎区域
.welcome-section {
	background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
	padding: 32px 32px;
	color: white;
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 32px;

	.welcome-content {
		.welcome-title {
			font-size: 28px;
			font-weight: 700;
			margin: 0 0 8px 0;
			text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		}

		.welcome-subtitle {
			font-size: 16px;
			opacity: 0.9;
			margin: 0;
			font-weight: 400;
		}
	}

	.welcome-time {
		text-align: right;

		.current-time {
			font-size: 14px;
			opacity: 0.8;
			font-weight: 500;
		}
	}
}

// 统计卡片区域
.stats-section {
	margin-bottom: 32px;

	.stats-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: 24px;

		.stat-card {
			background: white;
			border-radius: 12px;
			padding: 24px;
			box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
			border: 1px solid #f1f5f9;
			transition: all 0.2s ease;

			&:hover {
				box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
				transform: translateY(-2px);
			}

			.stat-content {
				display: flex;
				justify-content: space-between;
				align-items: flex-start;

				.stat-info {
					flex: 1;

					.stat-title {
						font-size: 14px;
						color: #64748b;
						margin: 0 0 8px 0;
						font-weight: 500;
					}

					.stat-value {
						font-size: 32px;
						font-weight: 700;
						color: #1e293b;
						margin: 0 0 8px 0;
						line-height: 1.2;
					}

					.stat-change {
						font-size: 13px;
						font-weight: 500;
						padding: 4px 8px;
						border-radius: 6px;
						display: inline-block;

						&.positive {
							color: #059669;
							background: #d1fae5;
						}

						&.negative {
							color: #dc2626;
							background: #fee2e2;
						}

						&.warning {
							color: #d97706;
							background: #fef3c7;
						}
					}
				}

				.stat-icon {
					width: 48px;
					height: 48px;
					border-radius: 10px;
					display: flex;
					align-items: center;
					justify-content: center;
					flex-shrink: 0;

					svg {
						width: 24px;
						height: 24px;
						fill: white;
					}

					&.orders {
						background: linear-gradient(135deg, #10b981 0%, #059669 100%);
					}

					&.revenue {
						background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
					}

					&.users {
						background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
					}

					&.stock {
						background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
					}
				}
			}
		}
	}
}

// 图表区域
.charts-section {
	margin-bottom: 32px;

	.charts-grid {
		display: grid;
		grid-template-columns: 2fr 1fr;
		gap: 24px;

		.chart-card {
			background: white;
			border-radius: 12px;
			box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
			border: 1px solid #f1f5f9;
			overflow: hidden;

			.chart-header {
				padding: 20px 24px;
				border-bottom: 1px solid #f1f5f9;
				display: flex;
				justify-content: space-between;
				align-items: center;

				.chart-title {
					font-size: 18px;
					font-weight: 600;
					color: #1e293b;
					margin: 0;
				}

				.chart-controls {
					display: flex;
					gap: 8px;

					.time-btn {
						padding: 6px 12px;
						border: 1px solid #e2e8f0;
						background: white;
						color: #64748b;
						border-radius: 6px;
						font-size: 12px;
						font-weight: 500;
						cursor: pointer;
						transition: all 0.2s ease;

						&:hover {
							border-color: #3b82f6;
							color: #3b82f6;
						}

						&.active {
							background: #3b82f6;
							border-color: #3b82f6;
							color: white;
						}
					}
				}

				.chart-legend {
					display: flex;
					gap: 16px;

					.legend-item {
        display: flex;
						align-items: center;
						gap: 6px;
						font-size: 12px;
						color: #64748b;

						.legend-dot {
							width: 8px;
							height: 8px;
							border-radius: 50%;

							&.pending {
								background: #f59e0b;
							}

							&.completed {
								background: #10b981;
							}

							&.cancelled {
								background: #ef4444;
							}
						}
					}
				}
			}

			.chart-content {
				padding: 24px;

				.chart-container {
        width: 100%;
					height: 300px;
				}
			}
		}
	}
}



// 响应式设计
@media (max-width: 1200px) {
	.charts-section .charts-grid {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 768px) {
	.dashboard-container {
		padding: 0;
	}

	.welcome-section {
		padding: 24px 0;
		flex-direction: column;
		text-align: center;
		gap: 16px;

		.welcome-content {
			.welcome-title {
				font-size: 24px;
			}

			.welcome-subtitle {
				font-size: 14px;
			}
		}

		.welcome-time {
			text-align: center;
		}
	}

	.stats-section .stats-grid {
		grid-template-columns: 1fr;
		gap: 16px;

		.stat-card {
			padding: 20px;

			.stat-content .stat-info .stat-value {
				font-size: 28px;
			}
		}
	}

	.charts-section {
		.charts-grid .chart-card {
			.chart-header {
				padding: 16px 20px;
				flex-direction: column;
				align-items: flex-start;
				gap: 12px;

				.chart-controls {
					width: 100%;
					justify-content: center;
				}

				.chart-legend {
					width: 100%;
					justify-content: center;
				}
			}

			.chart-content {
				padding: 20px;

				.chart-container {
					height: 250px;
				}
			}
		}
	}


}

@media (max-width: 480px) {
	.welcome-section {
		padding: 20px 0;

		.welcome-content .welcome-title {
			font-size: 20px;
		}
	}

	.stats-section .stats-grid {
		gap: 12px;

		.stat-card {
			padding: 16px;

			.stat-content {
				.stat-info .stat-value {
					font-size: 24px;
				}

				.stat-icon {
					width: 40px;
					height: 40px;

					svg {
						width: 20px;
						height: 20px;
					}
				}
			}
		}
	}

	.charts-section .charts-grid .chart-card {
		.chart-content {
			padding: 16px;

			.chart-container {
				height: 200px;
                }
            }
        }
    }
</style>
